import React, { useState } from 'react';
import { Col, Form, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import moment from 'moment';
import { FormGroup } from '../../../../components/form/FormGroup';
import InputLabel from '../../../../components/form/InputLabel';
import { ControlledInput } from '../../../../components/form/Input';
import { getRange } from '../../../../utils/dateUtils';
import { calculateTimeDifferenceInHours } from '../../../../utils/time';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { CopySimple } from '@phosphor-icons/react';
import './FormBody.css';
import Loading from '../Loading';

type Props = {
  absenceCategoriesGroupSelect: React.ReactNode;
  absenceCategorySelect: React.ReactNode;
  absenceRequestPolicySelect?: React.ReactNode;
  startDateInput: React.ReactNode;
  startDateTimeInput: React.ReactNode;
  endDateInput: React.ReactNode;
  endDateTimeInput: React.ReactNode;
  fileInput?: React.ReactNode;
  noteInput: React.ReactNode;
  absenceRequestPolicyRules?: React.ReactNode;
  specialRules?: React.ReactNode;
  requestingAmountBadge?: React.ReactNode;
  overlappingAbsences?: React.ReactNode;
  requireNote: boolean;
  extraFields?: React.ReactNode;
  // Updated props
  isHourly?: boolean;
  onTimeChange?: () => void;
  isEditingMode?: boolean;
};

const DateInputContainer: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => <div className="mb-2">{children}</div>;

type TimeFieldRowProps = {
  date: moment.Moment;
  dateKey: string;
  dateRange: moment.Moment[];
  onTimeChange?: () => void;
  isDisabled?: boolean;
};

const DEFAULT_START_TIME = '09:00';
const DEFAULT_END_TIME = '17:00';

const i18Path = `absences.myAbsences.table`;

const TimeFieldRow: React.FC<React.PropsWithChildren<TimeFieldRowProps>> = ({
  date,
  dateKey,
  dateRange,
  onTimeChange,
  isDisabled,
}) => {
  const t = useNamespacedTranslation(i18Path);
  const { t: tGeneral } = useTranslation();
  const formContext = useFormContext();
  const [isHovered, setIsHovered] = useState(false);

  if (!formContext) {
    return <Loading />;
  }

  const { watch, setValue } = formContext;

  const startTimeFieldName = `${dateKey}_startTime`;
  const endTimeFieldName = `${dateKey}_endTime`;

  const startTime = watch(startTimeFieldName) || DEFAULT_START_TIME;
  const endTime = watch(endTimeFieldName) || DEFAULT_END_TIME;

  const totalHours = calculateTimeDifferenceInHours(startTime, endTime);

  const handleApplyToAll = () => {
    dateRange.forEach((rangeDate) => {
      const rangeDateKey = rangeDate.format('YYYY-MM-DD');
      if (rangeDateKey !== dateKey) {
        const startTimeField = `${rangeDateKey}_startTime`;
        const endTimeField = `${rangeDateKey}_endTime`;
        setValue(startTimeField, startTime);
        setValue(endTimeField, endTime);
      }
    });

    onTimeChange?.();
  };

  const showApplyButton = dateRange.length > 1;

  return (
    <Row
      className="mb-2 align-items-center position-relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <Col sm={3}>
        <Form.Label className="mb-0">{date.format('ddd, MMM D')}</Form.Label>
      </Col>
      <Col sm={3}>
        <ControlledInput
          type="time"
          name={startTimeFieldName}
          placeholder={t('requestAbsence.form.startTime')}
          defaultValue={DEFAULT_START_TIME}
          onChange={onTimeChange}
          disabled={isDisabled}
        />
      </Col>
      <Col sm={3}>
        <ControlledInput
          type="time"
          name={endTimeFieldName}
          placeholder={t('requestAbsence.form.endTime')}
          defaultValue={DEFAULT_END_TIME}
          onChange={onTimeChange}
          disabled={isDisabled}
        />
      </Col>
      <Col sm={3} className="d-flex align-items-center justify-content-between">
        <div className="form-control-plaintext">
          <strong>
            {tGeneral('durationFormat.hours', { hours: totalHours })}
          </strong>
        </div>
        {showApplyButton && !isDisabled && (
          <OverlayTrigger
            overlay={<Tooltip id="copy-tooltip">{t('applyToAll')}</Tooltip>}>
            <CopySimple
              size={24}
              onClick={handleApplyToAll}
              className={`ms-2 text-primary ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                transition: 'opacity 0.2s ease-in-out',
                cursor: 'pointer',
              }}
            />
          </OverlayTrigger>
        )}
      </Col>
    </Row>
  );
};

const FormBody: React.FC<React.PropsWithChildren<Props>> = ({
  absenceCategoriesGroupSelect,
  absenceCategorySelect,
  absenceRequestPolicySelect,
  absenceRequestPolicyRules,
  startDateInput,
  startDateTimeInput,
  endDateInput,
  endDateTimeInput,
  fileInput,
  noteInput,
  specialRules,
  requestingAmountBadge,
  overlappingAbsences,
  requireNote,
  extraFields,
  isHourly = false,
  onTimeChange,
  isEditingMode = false,
}) => {
  const { t } = useTranslation();

  const formContext = useFormContext();

  if (!formContext) {
    return <Loading />;
  }

  const { watch } = formContext;
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const startMoment = startDate ? moment(startDate).startOf('day') : null;
  const endMoment = endDate ? moment(endDate).startOf('day') : null;

  const isStartDateSameOrBeforeEndDate =
    !!startMoment &&
    !!endMoment &&
    startMoment.isSameOrBefore(endMoment, 'day');

  const dateRange =
    isHourly && isStartDateSameOrBeforeEndDate
      ? getRange(startMoment, endMoment, 'day', true)
      : [];

  const renderDateAndTimeInputsSection = () => (
    <Row>
      <Col>
        <FormGroup>
          <InputLabel label={t('requestAbsence.form.period')} />

          <Row className={isHourly ? 'mb-3' : ''}>
            <Col>
              <Row>
                <Form.Label column className="col-auto">
                  {t('requestAbsence.form.startDate')}
                </Form.Label>
                <Col>
                  <DateInputContainer>{startDateInput}</DateInputContainer>
                  {!isHourly && startDateTimeInput}
                </Col>
              </Row>
            </Col>
            <Col>
              <Row>
                <Form.Label column className="col-auto">
                  {t('requestAbsence.form.endDate')}
                </Form.Label>
                <Col>
                  <DateInputContainer>{endDateInput}</DateInputContainer>
                  {!isHourly && endDateTimeInput}
                </Col>
              </Row>
            </Col>
          </Row>

          {dateRange.length > 0 && (
            <>
              <div className="mt-5">
                <h6 className="mb-3">
                  {t('requestAbsence.form.dailyTimeSchedule')}
                </h6>
              </div>

              {dateRange.map((date: moment.Moment) => {
                const dateKey = date.format('YYYY-MM-DD');
                return (
                  <TimeFieldRow
                    key={dateKey}
                    date={date}
                    dateKey={dateKey}
                    dateRange={dateRange}
                    onTimeChange={onTimeChange}
                    isDisabled={isEditingMode}
                  />
                );
              })}
            </>
          )}
        </FormGroup>
      </Col>
    </Row>
  );

  return (
    <Row className="justify-content-center">
      <Col>
        <Row>
          <Col>
            <FormGroup>{absenceCategoriesGroupSelect}</FormGroup>
            <FormGroup>{absenceCategorySelect}</FormGroup>
            {absenceRequestPolicySelect && (
              <FormGroup>{absenceRequestPolicySelect}</FormGroup>
            )}
          </Col>
        </Row>
        {absenceRequestPolicyRules && (
          <Row>
            <Col>{absenceRequestPolicyRules}</Col>
          </Row>
        )}
        {specialRules && (
          <Row>
            <Col>{specialRules}</Col>
          </Row>
        )}
        {renderDateAndTimeInputsSection()}
        {fileInput}
        <Row>
          <Col>
            <FormGroup>
              <InputLabel
                label={t('requestAbsence.form.note')}
                required={requireNote}
              />
              {noteInput}
            </FormGroup>
          </Col>
        </Row>
        {extraFields && (
          <Row>
            <Col>{extraFields}</Col>
          </Row>
        )}
        {requestingAmountBadge && (
          <Row>
            <Col>
              <FormGroup>{requestingAmountBadge}</FormGroup>
            </Col>
          </Row>
        )}
        {overlappingAbsences && (
          <Row>
            <Col>
              <FormGroup>{overlappingAbsences}</FormGroup>
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
};

export default FormBody;
